import React from 'react'
import { FaEdit } from 'react-icons/fa'

const FilePreview = ({
  fileObj,
  onUpdateDescription,
  onUpdateCustomName,
  onRemoveFile,
  onEditPhoto,
  formatFileSize
}) => {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <div className="flex items-start gap-4">
        <FilePreviewImage fileObj={fileObj} onEditPhoto={onEditPhoto} />
        <FileInfo
          fileObj={fileObj}
          onUpdateDescription={onUpdateDescription}
          onUpdateCustomName={onUpdateCustomName}
          onRemoveFile={onRemoveFile}
          formatFileSize={formatFileSize}
        />
      </div>
    </div>
  )
}

const FilePreviewImage = ({ fileObj, onEditPhoto }) => {
  const isImage = fileObj.file.type.startsWith('image/')

  return (
    <div className="flex-shrink-0 relative group">
      {fileObj.preview ? (
        <>
          <img
            src={fileObj.preview}
            alt="Preview"
            className="w-16 h-16 object-cover rounded-lg"
          />
          {isImage && onEditPhoto && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEditPhoto(fileObj.id)
              }}
              className="absolute top-1 right-1 bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-md shadow-lg transition-all"
              title="Edit Photo"
            >
              <FaEdit className="text-white text-xs" />
            </button>
          )}
        </>
      ) : (
        <VideoPreviewPlaceholder />
      )}
    </div>
  )
}

const VideoPreviewPlaceholder = () => (
  <div className="w-16 h-16 bg-gray-600 rounded-lg flex items-center justify-center">
    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 2h10l-1 12H8L7 6z" />
    </svg>
  </div>
)

const FileInfo = ({
  fileObj,
  onUpdateDescription,
  onUpdateCustomName,
  onRemoveFile,
  formatFileSize
}) => (
  <div className="flex-1 min-w-0">
    <FileHeader
      fileName={fileObj.file.name}
      onRemoveFile={() => onRemoveFile(fileObj.id)}
    />
    <FileMeta
      fileSize={fileObj.file.size}
      fileType={fileObj.file.type}
      formatFileSize={formatFileSize}
    />
    <CustomNameInput
      fileObj={fileObj}
      onUpdateCustomName={onUpdateCustomName}
    />
    <DescriptionInput
      fileObj={fileObj}
      onUpdateDescription={onUpdateDescription}
    />
  </div>
)

const FileHeader = ({ fileName, onRemoveFile }) => (
  <div className="flex items-center justify-between mb-2">
    <p className="text-white font-medium truncate">
      {fileName}
    </p>
    <button
      onClick={onRemoveFile}
      className="text-red-400 hover:text-red-300 transition-colors"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
)

const FileMeta = ({ fileSize, fileType, formatFileSize }) => (
  <p className="text-gray-400 text-sm mb-3">
    {formatFileSize(fileSize)} • {fileType}
  </p>
)

const CustomNameInput = ({ fileObj, onUpdateCustomName }) => (
  <div className="space-y-2 mb-4">
    <label className="text-gray-300 text-sm font-medium">
      🏷️ Media Name
    </label>
    <input
      type="text"
      placeholder="Enter a name for this media..."
      value={fileObj.customName}
      onChange={(e) => onUpdateCustomName(fileObj.id, e.target.value)}
      className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
    />
    <p className="text-gray-400 text-xs">
      This name will be displayed instead of the filename
    </p>
  </div>
)

const DescriptionInput = ({ fileObj, onUpdateDescription }) => (
  <div className="space-y-2">
    <label className="text-gray-300 text-sm font-medium">
      📝 Caption/Description
    </label>
    <input
      type="text"
      placeholder="Describe this photo or video..."
      value={fileObj.description}
      onChange={(e) => onUpdateDescription(fileObj.id, e.target.value)}
      className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
    />
    <p className="text-gray-400 text-xs">
      This caption will appear in your photo carousel and gallery
    </p>
  </div>
)

export default FilePreview